/**
 * MarkupSettingsDialog (Story 17.2 — Epic 17 Ad Spend Markup)
 *
 * Per-client markup settings as a CENTERED MODAL DIALOG (directive §4.1 —
 * NEVER a side Sheet; the *Dialog.tsx naming convention, not the legacy
 * *Sheet.tsx suffix). Built on the §4.2 scaffold: @radix-ui/react-dialog +
 * AnimatePresence forceMount portal, bg-black/35 overlay, rounded-3xl
 * border-[0.5px] p-8 content, dialog enter/exit {opacity, scale:0.95, y:10}.
 *
 * Two sections inside one dialog:
 *  - GLOBAL DEFAULT: type segmented control (Percentage / Fixed $) + value
 *    input with %/$ adornment. Clearing the value = "No markup" (the global
 *    row is dropped from the PUT payload).
 *  - PER-INTEGRATION OVERRIDES: one row per CONNECTED ad-spend platform,
 *    DEDUPED by type (Trap #1 — markup is keyed by integration TYPE; a client
 *    can hold multiple accounts per platform, render the platform ONCE). A
 *    stored override whose platform is no longer connected still renders,
 *    flagged "not connected" (settings never silently vanish). Each row's
 *    Override Switch reveals that row's own type+value controls.
 *
 * Writes the full set via PUT /api/clients/:id/markups (replace semantics —
 * Switch off = no override = the row is omitted = the server deletes it).
 *
 * §4.6: always-mounted at the call site (no {open && ...} guard); form state
 * resets on OPEN, not close. Close is blocked while the save is pending.
 * All visible children stagger per §6.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button, Input, Switch } from "@provenleap/ui";
import { Percent, WarningCircle } from "@phosphor-icons/react";
import { useClientIntegrations } from "../../hooks/useClientIntegrations";
import {
  useClientMarkups,
  useSaveClientMarkups,
  type ClientMarkupRow,
  type MarkupType,
} from "../../hooks/useClientMarkups";
import { AD_SPEND_PLATFORMS, platformBadge } from "../../lib/integrations";
import type { ToastType } from "../../hooks/useToast";

// ============================================================================
// Props
// ============================================================================

interface MarkupSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName?: string;
  showToast: (type: ToastType, message: string) => void;
}

// ============================================================================
// Validation (mirrors the server AC #3 — range + 2-decimal)
// ============================================================================

const AD_SPEND_SET = new Set<string>(AD_SPEND_PLATFORMS);

function hasAtMostTwoDecimals(value: number): boolean {
  return Math.abs(Math.round(value * 100) - value * 100) < 1e-9;
}

/** Returns an inline error string for a raw value field, or null if valid. */
function validateValue(raw: string, type: MarkupType): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null; // empty = "no markup" for this scope (caller drops it)
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return "Enter a number";
  if (n <= 0) return "Must be greater than 0";
  const max = type === "percentage" ? 500 : 1_000_000;
  if (n > max) {
    return type === "percentage" ? "Max 500%" : "Max $1,000,000";
  }
  if (!hasAtMostTwoDecimals(n)) return "At most 2 decimals";
  return null;
}

/** "15%" / "$500/mo" display formatting (AC #11). */
export function formatMarkup(type: MarkupType, value: number): string {
  if (type === "percentage") {
    return `${Number.isInteger(value) ? value : value.toFixed(2)}%`;
  }
  return `$${Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2)}/mo`;
}

// ============================================================================
// Local form state
// ============================================================================

interface ScopeFormState {
  type: MarkupType;
  /** Raw string so the field can be cleared to the "no markup" state. */
  value: string;
}

interface OverrideFormState extends ScopeFormState {
  enabled: boolean;
}

/** One render row: a connected/known platform plus its override form state. */
interface OverrideRow {
  integrationType: string;
  displayName: string;
  logoSrc: string | null;
  /** Stored but the platform is no longer a connected integration. */
  disconnected: boolean;
}

function emptyScope(): ScopeFormState {
  return { type: "percentage", value: "" };
}

// ============================================================================
// Segmented control (compact §4.3 — keeps the dialog at max-w-md)
// ============================================================================

function TypeSegmentedControl({
  value,
  onChange,
  idBase,
}: {
  value: MarkupType;
  onChange: (t: MarkupType) => void;
  idBase: string;
}) {
  const options: { key: MarkupType; label: string }[] = [
    { key: "percentage", label: "Percentage" },
    { key: "fixed", label: "Fixed $" },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Markup type"
      className="grid grid-cols-2 gap-1 rounded-2xl bg-muted/40 p-1 shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`${idBase}-type-${opt.key}`}
            onClick={() => onChange(opt.key)}
            className={`cursor-pointer rounded-xl px-3 py-1.5 text-[13px] font-medium transition-all ${
              active
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
            style={active ? { border: "0.5px solid var(--hairline)" } : undefined}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Value field with %/$ adornment + inline error
// ============================================================================

function ValueField({
  type,
  value,
  onChange,
  error,
  idBase,
  hint,
}: {
  type: MarkupType;
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  idBase: string;
  hint?: string;
}) {
  const adornment = type === "percentage" ? "%" : "$";
  const isPrefix = type === "fixed";
  return (
    <div className="space-y-1">
      <div className="relative">
        {isPrefix && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground">
            {adornment}
          </span>
        )}
        <Input
          type="text"
          inputMode="decimal"
          data-testid={`${idBase}-value`}
          aria-label={type === "percentage" ? "Markup percentage" : "Markup amount"}
          aria-invalid={error ? true : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={type === "percentage" ? "e.g. 15" : "e.g. 500"}
          className={`rounded-2xl shadow-xs ${isPrefix ? "pl-7" : "pr-8"}`}
        />
        {!isPrefix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground">
            {adornment}
          </span>
        )}
      </div>
      {error ? (
        <p className="text-[12px] text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function MarkupSettingsDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  showToast,
}: MarkupSettingsDialogProps) {
  const { markups, isLoading: markupsLoading } = useClientMarkups(
    open ? clientId : undefined
  );
  const { integrations, isLoading: integrationsLoading } = useClientIntegrations(
    open ? clientId : undefined
  );
  const saveMutation = useSaveClientMarkups(clientId);
  const pending = saveMutation.isPending;

  const [globalForm, setGlobalForm] = useState<ScopeFormState>(emptyScope);
  const [overrideForms, setOverrideForms] = useState<
    Record<string, OverrideFormState>
  >({});

  // Connected ad-spend platforms, DEDUPED by type (Trap #1).
  const connectedAdTypes = useMemo(() => {
    const set = new Set<string>();
    for (const i of integrations) {
      if (AD_SPEND_SET.has(i.type)) set.add(i.type);
    }
    return set;
  }, [integrations]);

  // §4.6 — reset/seed form state ONCE per open session, the FIRST time the data
  // has finished loading (never on close, which flashes mid-exit; never on a
  // mid-edit refetch, which would discard in-progress edits). `seededRef` marks
  // that this session has been seeded; it clears on close so the next open
  // re-seeds from the freshest fetch. Gating on !loading avoids the race where
  // the dialog opens before the markups query resolves and would otherwise seed
  // an empty form (dropping stored overrides until reopen).
  const seededRef = useRef(false);
  const dataLoading = markupsLoading || integrationsLoading;
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current || dataLoading) return;
    seededRef.current = true;
    {
      const byScope = new Map<string | null, ClientMarkupRow>();
      for (const m of markups) byScope.set(m.integrationType, m);

      const global = byScope.get(null);
      setGlobalForm(
        global
          ? { type: global.markupType, value: String(global.markupValue) }
          : emptyScope()
      );

      const next: Record<string, OverrideFormState> = {};
      // Every connected ad platform gets a row (off by default).
      for (const type of connectedAdTypes) {
        next[type] = { enabled: false, type: "percentage", value: "" };
      }
      // Stored overrides seed/enable their rows (incl. now-disconnected platforms).
      for (const m of markups) {
        if (m.integrationType === null) continue;
        next[m.integrationType] = {
          enabled: true,
          type: m.markupType,
          value: String(m.markupValue),
        };
      }
      setOverrideForms(next);
    }
  }, [open, dataLoading, markups, connectedAdTypes]);

  // Render rows: connected platforms ∪ stored-override platforms (deduped),
  // alphabetical, disconnected ones flagged.
  const overrideRows = useMemo<OverrideRow[]>(() => {
    const types = new Set<string>(connectedAdTypes);
    for (const key of Object.keys(overrideForms)) types.add(key);
    return Array.from(types)
      .sort((a, b) => a.localeCompare(b))
      .map((type) => {
        const badge = platformBadge(type);
        return {
          integrationType: type,
          displayName: badge?.displayName ?? type,
          logoSrc: badge?.logoSrc ?? null,
          disconnected: !connectedAdTypes.has(type),
        };
      });
  }, [connectedAdTypes, overrideForms]);

  // ── Validation ──────────────────────────────────────────────────────────
  const globalError = validateValue(globalForm.value, globalForm.type);
  const overrideErrors = useMemo(() => {
    const errs: Record<string, string | null> = {};
    for (const [type, form] of Object.entries(overrideForms)) {
      if (!form.enabled) {
        errs[type] = null;
        continue;
      }
      // An enabled override with an empty value is invalid (must set a value).
      if (form.value.trim() === "") {
        errs[type] = "Enter a value";
        continue;
      }
      errs[type] = validateValue(form.value, form.type);
    }
    return errs;
  }, [overrideForms]);

  const hasErrors =
    globalError !== null ||
    Object.values(overrideErrors).some((e) => e !== null);

  // ── Close guard (§4.2) ────────────────────────────────────────────────────
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && pending) return;
      onOpenChange(next);
    },
    [pending, onOpenChange]
  );

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (hasErrors) return;

    const globalTrimmed = globalForm.value.trim();
    const global =
      globalTrimmed === ""
        ? null
        : { markupType: globalForm.type, markupValue: Number(globalTrimmed) };

    const overrides = Object.entries(overrideForms)
      .filter(([, f]) => f.enabled && f.value.trim() !== "")
      .map(([integrationType, f]) => ({
        integrationType,
        markupType: f.type,
        markupValue: Number(f.value.trim()),
      }));

    try {
      await saveMutation.mutateAsync({ global, overrides });
      showToast("success", "Markup settings saved");
      onOpenChange(false);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to save markup settings"
      );
    }
  }, [hasErrors, globalForm, overrideForms, saveMutation, showToast, onOpenChange]);

  function setOverride(type: string, patch: Partial<OverrideFormState>) {
    setOverrideForms((prev) => ({
      ...prev,
      [type]: { ...(prev[type] ?? { enabled: false, type: "percentage", value: "" }), ...patch },
    }));
  }

  // Stagger sections: title(0) → global(1) → overrides header(2) → rows(3..) → footer.
  let staggerIndex = 0;
  const nextDelay = () => 0.05 * (staggerIndex++ + 1);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100vw-2rem)] sm:max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {/* Header */}
                <motion.div
                  className="flex flex-col space-y-1.5 text-left"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: nextDelay() }}
                >
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                    <Percent size={18} className="text-foreground" />
                    Markup settings
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {clientName
                      ? `Set the markup applied to ${clientName}'s ad spend in reports.`
                      : "Set the markup applied to this client's ad spend in reports."}
                  </DialogPrimitive.Description>
                </motion.div>

                {/* Global default */}
                <motion.div
                  className="mt-6 space-y-3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: nextDelay() }}
                >
                  <p className="text-[13px] font-medium text-foreground">
                    Global default
                  </p>
                  <TypeSegmentedControl
                    idBase="markup-global"
                    value={globalForm.type}
                    onChange={(type) =>
                      setGlobalForm((f) => ({ ...f, type }))
                    }
                  />
                  <ValueField
                    idBase="markup-global"
                    type={globalForm.type}
                    value={globalForm.value}
                    onChange={(value) =>
                      setGlobalForm((f) => ({ ...f, value }))
                    }
                    error={globalError}
                    hint="Applied to all advertising platforms unless overridden. Leave empty for no markup."
                  />
                </motion.div>

                {/* Per-integration overrides */}
                <motion.div
                  className="mt-6"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: nextDelay() }}
                >
                  <p className="text-[13px] font-medium text-foreground">
                    Per-platform overrides
                  </p>
                </motion.div>

                <div className="mt-2">
                  {overrideRows.length === 0 ? (
                    <motion.p
                      className="text-[13px] text-muted-foreground py-3"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                      data-testid="markup-overrides-empty"
                    >
                      No advertising integrations connected.
                    </motion.p>
                  ) : (
                    overrideRows.map((row) => {
                      const form =
                        overrideForms[row.integrationType] ?? {
                          enabled: false,
                          type: "percentage" as MarkupType,
                          value: "",
                        };
                      const error = overrideErrors[row.integrationType] ?? null;
                      const rowDelay =
                        overrideRows.length > 6 ? 0.03 : 0.05;
                      const idBase = `markup-override-${row.integrationType}`;
                      return (
                        <motion.div
                          key={row.integrationType}
                          data-testid="markup-override-row"
                          data-platform={row.integrationType}
                          className="border-b-[0.5px] border-input py-3 last:border-b-0"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: rowDelay * (staggerIndex++ + 1) }}
                        >
                          <div className="flex items-center gap-3">
                            {row.logoSrc && (
                              <img
                                src={row.logoSrc}
                                alt={row.displayName}
                                className="h-4 w-4 rounded-sm shrink-0"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-medium text-foreground truncate">
                                {row.displayName}
                              </p>
                              {row.disconnected && (
                                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                                  <WarningCircle size={12} className="text-muted-foreground" />
                                  Not connected
                                </p>
                              )}
                            </div>
                            <Switch
                              data-testid={`${idBase}-switch`}
                              aria-label={`Override markup for ${row.displayName}`}
                              checked={form.enabled}
                              onCheckedChange={(checked) =>
                                setOverride(row.integrationType, { enabled: checked })
                              }
                            />
                          </div>

                          <AnimatePresence initial={false}>
                            {form.enabled && (
                              <motion.div
                                className="mt-3 space-y-3 pl-7"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2, ease: "easeInOut" }}
                              >
                                <TypeSegmentedControl
                                  idBase={idBase}
                                  value={form.type}
                                  onChange={(type) =>
                                    setOverride(row.integrationType, { type })
                                  }
                                />
                                <ValueField
                                  idBase={idBase}
                                  type={form.type}
                                  value={form.value}
                                  onChange={(value) =>
                                    setOverride(row.integrationType, { value })
                                  }
                                  error={error}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                <motion.div
                  className="flex justify-end gap-3 mt-6"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * (staggerIndex++ + 1) }}
                >
                  <Button
                    variant="outline"
                    type="button"
                    className="rounded-2xl text-[13px] bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    onClick={() => handleOpenChange(false)}
                    disabled={pending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="brand"
                    type="button"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={handleSave}
                    disabled={pending || hasErrors}
                    data-testid="markup-save"
                  >
                    {pending ? "Saving…" : "Save"}
                  </Button>
                </motion.div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
